import styled from '@emotion/styled';
import {
  duration,
  easing,
  layout,
  palette,
  radius,
  shadow,
  spacing,
  transition,
} from '../../../style-utils/theme';
import { down, touch } from '../../../style-utils/media-queries';
import { ActiveIndicator } from '../../../motion';

export const NavbarContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin: ${spacing.xxl} ${spacing.lg};

  ${down('md')} {
    margin: ${spacing.md} ${spacing.sm};
  }
`;

export const NavTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
  margin-bottom: ${spacing.md};
`;

/**
 * A step in the wizard rail. Reflects state via colour + the sliding iris
 * accent bar (StepIndicator below — one element that moves between steps; never
 * font-weight, which reflows the label). Complete steps read in ink, the
 * current step gets an iris-tint pill, upcoming steps stay muted.
 */
export const NavItem = styled.button<{ active?: boolean; complete?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.xs} ${spacing.sm};
  border-radius: ${radius.sm};
  cursor: pointer;
  width: 100%;
  /* The desk's step rail is pressed on a tablet: 40px was the text and its
     padding, under the 44px touch floor (J-3.6 named each one). The floor
     only on touch, so the rail under a mouse keeps its density. */
  ${touch} {
    min-height: ${layout.touchTarget};
  }
  border: none;
  text-align: left;
  font: inherit;
  color: ${({ active, complete }) =>
    active ? palette.iris : complete ? palette.inkSoft : palette.slate};
  background: ${({ active }) => (active ? palette.irisTint : 'transparent')};
  transition: ${transition.colors}, transform ${duration.fast} ${easing.spring};

  & svg {
    flex-shrink: 0;
    transition: transform ${duration.fast} ${easing.standard};
  }

  &:hover {
    background: ${({ active }) =>
      active ? palette.irisTint : palette.irisTintSoft};
    color: ${({ active }) => (active ? palette.lagoon : palette.inkSoft)};
  }

  /* KEYBOARD GETS WHAT THE POINTER GETS. This control styled :hover and nothing
     for focus, so it acknowledged a mouse and ignored a keyboard. */
  &:focus-visible {
    outline: none;
    box-shadow: ${shadow.focus};
  }
  &:hover svg {
    transform: translateX(2px);
  }

  &:disabled {
    cursor: default;
  }

  &:disabled:hover {
    background: ${({ active }) => (active ? palette.irisTint : 'transparent')};
    color: ${({ active }) => (active ? palette.lagoon : palette.slate)};
  }
`;

/* The accent bar, rendered inside the current step only; framer's layoutId
   carries it from the previous step with the indicator spring. top/height
   arithmetic rather than a translate: the layout animation owns the transform. */
export const StepIndicator = styled(ActiveIndicator)`
  position: absolute;
  left: 0;
  top: 20%;
  width: 3px;
  height: 60%;
  border-radius: ${radius.pill};
  background: ${palette.iris};
  pointer-events: none;
`;
